import { Spinner } from "@via/components/loading/loading";
import SelectComponent from "@via/components/select/select.tsx";
import {
  useGetAdminOrganizationCredentials,
  useUpdateAdminOrganizationRunEnvironment,
} from "@via/pages/account/admin/organizations/organizations.api.tsx";
import { AdminOrganizationRunEnvironment } from "common";
import { useState, type JSX } from "react";

/**
 * The one thing a platform managed run environment still accepts: who its AWS Batch jobs are
 * submitted as.
 *
 * A narrow form rather than the full one, because the server refuses the whole request if any
 * other field arrives on such a row (spec decision 6). The reason this carve-out exists at all is
 * that neither half of the machinery can supply a submitter: the boundary does not carry
 * credentials, and the shared template row is global, so setting one there would hand it to every
 * organization (decision 10).
 *
 * The two modes are exclusive. An explicit organization credential is the predetermined policy;
 * the instance role is for a backend that runs on AWS itself.
 */
export default function AwsSubmitterForm({
  organizationId,
  runEnvironment,
  onDone,
}: {
  organizationId: string;
  runEnvironment: AdminOrganizationRunEnvironment;
  onDone: () => void;
}): JSX.Element {
  const [amazonCreId, setAmazonCreId] = useState<number | null>(
    runEnvironment.amazonCreId
  );
  const [useInstanceRole, setUseInstanceRole] = useState(
    runEnvironment.awsUseInstanceRole
  );

  const { data: credentials, isLoading: isCredentialsLoading } =
    useGetAdminOrganizationCredentials(organizationId, !!organizationId);
  const { mutateAsync: update, isPending } =
    useUpdateAdminOrganizationRunEnvironment();

  const options = (credentials?.amazon || []).map(({ id, name }) => ({
    label: name,
    value: id,
  }));

  const onSave = async (): Promise<void> => {
    await update({
      organizationId,
      runEnvironmentId: runEnvironment.id,
      systemGenerated: true,
      payload: {
        amazonCreId: useInstanceRole ? null : amazonCreId,
        awsUseInstanceRole: useInstanceRole,
      } as never,
    });
    onDone();
  };

  return (
    <div
      data-testid="aws-submitter-form"
      className="space-y-4 rounded-md border border-gray-200 p-4">
      <div>
        <div className="text-sm font-medium text-gray-900">
          AWS submitter for {runEnvironment.name}
        </div>
        <p className="mt-1 text-sm text-gray-600">
          This environment is managed by the platform, so only the identity its
          AWS Batch jobs are submitted as can be changed here.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-900">
          Organization AWS credential
        </label>
        <div className="mt-2" data-testid="submitter-amazon-id">
          <SelectComponent
            name="amazonCreId"
            options={options}
            value={options.find(option => option.value === amazonCreId) ?? null}
            onChange={selected =>
              setAmazonCreId(
                (selected as { value: number } | null)?.value ?? null
              )
            }
            isLoading={isCredentialsLoading}
            readOnly={useInstanceRole}
            placeholder="Select an AWS credential"
          />
        </div>
      </div>

      <button
        type="button"
        aria-pressed={useInstanceRole}
        onClick={() => setUseInstanceRole(current => !current)}
        className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
        Use instance role
      </button>
      <div className="text-xs text-gray-500">
        {useInstanceRole
          ? "Jobs submit as the role attached to the backend host. Available only where the backend runs on AWS."
          : "Jobs submit as the organization's assume role credential."}
      </div>

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onDone}
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void onSave()}
          disabled={isPending || (!useInstanceRole && !amazonCreId)}
          className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50">
          <Spinner loading={isPending}>Save submitter</Spinner>
        </button>
      </div>
    </div>
  );
}
